<template>
  <div>
    <Head :title="title" />
    <div class="flex items-center justify-between mb-6">
      <search-input v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset" />
      <Link class="btn-indigo" :href="route('escalation.create')">
        <span class="hidden md:inline"> {{ __('Create Escalation Matrix') }} </span>
      </Link>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold">
            <th class="pb-4 pt-6 px-6">{{ __('Name') }}</th>
            <th class="pb-4 pt-6 px-6">{{ __('Priority') }}</th>
            <th class="pb-4 pt-6 px-6">{{ __('Status') }}</th>
            <th class="pb-4 pt-6 px-6">{{ __('Escalate After') }}</th>
            <th class="pb-4 pt-6 px-6">{{ __('Notification Method') }}</th>
            <th class="pb-4 pt-6 px-6">{{ __('Users') }}</th>
            <th class="pb-4 pt-6 px-6">{{ __('Roles') }}</th>
            <th class="pb-4 pt-6 px-6">{{ __('Is Status') }}</th>
            <th class="pb-4 pt-6 px-6" colspan="2">{{ __('Actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="matrice in matrices.data" :key="matrice.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="border-t">
              <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="route('escalation.edit', matrice.id)">
                {{ matrice.name }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="route('escalation.edit', matrice.id)" tabindex="-1">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="getPriorityClass(matrice.priority)"
                >
                  {{ formatPriority(matrice.priority.name) }}
                </span>
              </Link>
            </td>

            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="route('escalation.edit', matrice.id)" tabindex="-1">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="getPriorityClass(matrice.status.name)"
                >
                  {{ formatPriority(matrice.status.name) }}
                </span>
              </Link>
            </td>



            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="route('escalation.edit', matrice.id)" tabindex="-1">
                {{ matrice.escalate_after_minutes }} {{ __('minutes') }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="route('escalation.edit', matrice.id)" tabindex="-1">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="getNotificationClass(matrice.notification_method)"
                >
                  {{ formatNotificationMethod(matrice.notification_method) }}
                </span>
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="route('escalation.edit', matrice.id)" tabindex="-1">
                <span
                  v-if="matrice.escalate_to_users && matrice.escalate_to_users.length > 0"
                  class="text-sm text-gray-600"
                >
                  {{ matrice.escalate_to_users.length }} {{ __('user(s)') }}
                </span>
                <span v-else class="text-sm text-gray-400">{{ __('None') }}</span>
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="route('escalation.edit', matrice.id)" tabindex="-1">
                <span
                  v-if="matrice.escalate_to_roles && matrice.escalate_to_roles.length > 0"
                  class="text-sm text-gray-600"
                >
                  {{ matrice.escalate_to_roles.length }} {{ __('role(s)') }}
                </span>
                <span v-else class="text-sm text-gray-400">{{ __('None') }}</span>
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4" :href="route('escalation.edit', matrice.id)" tabindex="-1">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="matrice.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                >
                  {{ matrice.is_active ? __('Active') : __('Inactive') }}
                </span>
              </Link>
            </td>
            <td class="w-px border-t">
              <Link class="flex items-center px-4" :href="route('escalation.edit', matrice.id)" tabindex="-1">
                <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
              </Link>
            </td>
          </tr>
          <tr v-if="matrices.data.length === 0">
            <td class="px-6 py-4 border-t" colspan="9">{{ __('No matrices found.') }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination class="mt-6" :links="matrices.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchInput from '@/Shared/SearchInput'

export default {
  components: {
    SearchInput,
    Head,
    Icon,
    Link,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    matrices: Object,
    title: String,
  },
  data() {
    return {
      form: {
        search: (this.filters && this.filters.search) ? this.filters.search : '',
      },
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(this.route('escalation.index'), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    getPriorityClass(priority) {
      const classes = {
        'low': 'bg-gray-100 text-gray-800',
        'medium': 'bg-yellow-100 text-yellow-800',
        'high': 'bg-orange-100 text-orange-800',
        'critical': 'bg-red-100 text-red-800',
      }
      return classes[priority] || 'bg-gray-100 text-gray-800'
    },
    formatPriority(priority) {
      const formatted = {
        'low': this.__('Low'),
        'medium': this.__('Medium'),
        'high': this.__('High'),
        'critical': this.__('Critical'),
      }
      return formatted[priority] || priority
    },
    getNotificationClass(method) {
      const classes = {
        'email': 'bg-blue-100 text-blue-800',
        'sms': 'bg-green-100 text-green-800',
        'slack': 'bg-purple-100 text-purple-800',
      }
      return classes[method] || 'bg-gray-100 text-gray-800'
    },
    formatNotificationMethod(method) {
      const formatted = {
        'email': this.__('Email'),
        'sms': this.__('SMS'),
        'slack': this.__('Slack'),
      }
      return formatted[method] || method
    },
  },
}
</script>
